import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { identify } from '../lib/amplitude';
import { fetchMe } from '../queries/account';

/**
 * Who the analytics are about.
 *
 * Firebase says a browser is signed in; GET /v1/me says which Slate account
 * that is, and the Slate account is what every client reports against. So the
 * identify waits for /v1/me rather than the Firebase session, and sends the
 * Postgres uuid, never the Firebase uid.
 *
 * Mounted once, above the router, so a share page opened by a signed-in
 * person identifies them the same way the library does. It renders nothing;
 * the query shares its key with the rest of the app, so this costs no extra
 * request on any page that already asks who you are.
 */
export function AnalyticsIdentity(): null {
  const { user } = useAuth();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  const account = me.data ?? null;
  const userId = account?.id ?? null;
  const email = account?.email ?? null;
  const name = account?.name ?? null;
  const plan: 'free' | 'premium' = account?.paidSeat ? 'premium' : 'free';
  // Where the plan came from, as /v1/me reports it: the API knows whether a
  // seat was bought or given, and the team's seat counts cannot say.
  const planSource: 'seat' | 'comp' | 'none' = account?.planSource ?? 'none';
  const teams = account?.memberships.length ?? 0;

  useEffect(() => {
    if (!userId) return;
    identify({ userId, email, name, plan, planSource, teams });
  }, [email, name, plan, planSource, teams, userId]);

  return null;
}
